import classNames from "classnames"
import { HTMLAttributes, useCallback, useEffect, useState } from "react"
import { ButtonPrimary, ButtonStatus } from "@nova/component-btn"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { FirePerimeterPluginActionMethodAutoAccordion } from "./FirePerimeterPluginActionMethodAutoAccordion"
import { useFirePerimeterAutoCategories } from "./useFirePerimeterAutoCategories"
import * as Icons from "@nova/component-icons"
import { FirePerimterPluginActionMethodAutoOffsetInput } from "./FirePerimterPluginActionMethodAutoOffsetInput"
import { LayersSearchBar } from "themes/default-tailwind/layers/LayersSearchBar"
import { autoCreatePerimeterPolygon } from "./module"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    addMultiplePolygonsToFirePerimeterPreview,
    selectEditingFirePerimeterId,
    setFirePerimeterPreviewPolygon,
    updatePerimeterCanConfirm,
} from "plugins/DrawPerimeter/FirePerimeterSlice"
import { selectFirePerimeterById } from "features/entity-caching/fire-perimeters/selectors"
import { submitPerimeterAuto } from "features/entity-caching/fire-perimeters/thunks"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { createSingleTaskRunner } from "nodash"

const generatePolygonsRunner = createSingleTaskRunner()
const DEFAULT_OFFSET_IN_METERS = 3.048 // 10 Feet
/**
 * Shared component used in both the layer panel and inspector panel.
 */
export const FirePerimeterAuto = ({
    shouldShowConfirmControls,
}: {
    shouldShowConfirmControls: boolean
} & HTMLAttributes<HTMLDivElement>) => {
    const [offset, setOffset] = useState(DEFAULT_OFFSET_IN_METERS)
    const [isGeneratingPreviewPolygons, setIsGeneratingPreviewPolygons] = useState(false)
    const dispatch = useAppDispatch()
    const [selectedHotspotFlightIds, setSelectedHotspotFlightIds] = useState(new Set<string>())
    const [selectedFlightPathFlightIds, setSelectedFlightPathFlightIds] = useState(
        new Set<string>()
    )
    const [selectedLineIds, setSelectedLineIds] = useState(new Set<string>())
    const [selectedMergedFlightIds, setSelectedMergedFlightIds] = useState(new Set<string>())
    const existingPerimeterId = useAppSelector(selectEditingFirePerimeterId())
    const existingPerimeter = useAppSelector(selectFirePerimeterById(existingPerimeterId || ""))

    useEffectUnsafe(() => {
        const generatePreviewPolygons = async (offset: number) => {
            setIsGeneratingPreviewPolygons(true)

            const prePreviewPolygon = await autoCreatePerimeterPolygon(
                {
                    hotspotFlightIds: selectedHotspotFlightIds,
                    flightPathFlightIds: selectedFlightPathFlightIds,
                    lineIds: selectedLineIds,
                    mergedFlightIDs: selectedMergedFlightIds,
                    onError: (error) => {
                        if (error !== "aborted") {
                            setIsGeneratingPreviewPolygons(false)
                        }
                    },
                },
                offset !== undefined ? offset : 1
            )

            if (!prePreviewPolygon) {
                console.error("Failed to create preview polygon via auto method.")
                return [...(existingPerimeter ? existingPerimeter.polygons : [])]
            }

            dispatch(setFirePerimeterPreviewPolygon(prePreviewPolygon))
            if (existingPerimeter) {
                dispatch(
                    addMultiplePolygonsToFirePerimeterPreview(
                        existingPerimeter.polygons.map((polygon) => polygon.coordinates)
                    )
                )
            }

            setIsGeneratingPreviewPolygons(false)

            return [
                ...(prePreviewPolygon.length >= 3 ? [{ coordinates: prePreviewPolygon }] : []),
                ...(existingPerimeter ? existingPerimeter.polygons : []),
            ]
        }
        generatePolygonsRunner.run(() => generatePreviewPolygons(offset))
    }, [
        offset,
        selectedFlightPathFlightIds,
        selectedHotspotFlightIds,
        selectedLineIds,
        selectedMergedFlightIds,
    ])

    const canConfirm =
        selectedHotspotFlightIds.size +
            selectedFlightPathFlightIds.size +
            selectedLineIds.size +
            selectedMergedFlightIds.size >
            0 && !isGeneratingPreviewPolygons

    useEffect(() => {
        dispatch(updatePerimeterCanConfirm(canConfirm))
    }, [canConfirm, dispatch])

    const [openCategories, setOpenCategories] = useState(new Set<string>())
    const [searchContents, setSearchContents] = useState<string>("")
    const [unitDropdownIsOpen, setUnitDropdownIsOpen] = useState(false)

    const { hotspotFlights, flightsWithFlightPaths, lines, mergedFlights } =
        useFirePerimeterAutoCategories({
            searchContents,
        })

    const setCategoryIsOpen = useCallback((category: string, isOpen: boolean) => {
        setOpenCategories((prev) => {
            const openCategories = new Set(prev)
            if (isOpen) openCategories.add(category)
            else openCategories.delete(category)
            return openCategories
        })
    }, [])

    return (
        <div
            className={classNames("flex flex-col overflow-hidden min-h-0")}
            style={{ paddingBottom: unitDropdownIsOpen ? 100 : 0 }}
        >
            <LayersSearchBar
                searchContents={searchContents}
                setSearchContents={setSearchContents}
            />

            <ScrollableArea shouldPreventScrollbarShifting={true} className="-mr-3 pr-3 mt-2">
                {hotspotFlights.length > 0 && (
                    <FirePerimeterPluginActionMethodAutoAccordion
                        title="Hotspots"
                        entities={hotspotFlights}
                        onEntityClicked={({ id }, isSelected) =>
                            setSelectedHotspotFlightIds((prev) => {
                                if (isSelected) prev.add(id)
                                else prev.delete(id)
                                return new Set(prev)
                            })
                        }
                        isOpen={openCategories.has("hotspots")}
                        setIsOpen={(isOpen) => setCategoryIsOpen("hotspots", isOpen)}
                    />
                )}

                {mergedFlights.length > 0 && (
                    <FirePerimeterPluginActionMethodAutoAccordion
                        title="Merged Hotspots"
                        entities={mergedFlights}
                        onEntityClicked={({ id }, isSelected) =>
                            setSelectedMergedFlightIds((prev) => {
                                if (isSelected) prev.add(id)
                                else prev.delete(id)
                                return new Set(prev)
                            })
                        }
                        isOpen={openCategories.has("merged-flights")}
                        setIsOpen={(isOpen) => setCategoryIsOpen("merged-flights", isOpen)}
                    />
                )}

                {flightsWithFlightPaths.length > 0 && (
                    <FirePerimeterPluginActionMethodAutoAccordion
                        title="Flight Paths"
                        entities={flightsWithFlightPaths}
                        onEntityClicked={({ id }, isSelected) =>
                            setSelectedFlightPathFlightIds((prev) => {
                                if (isSelected) prev.add(id)
                                else prev.delete(id)
                                return new Set(prev)
                            })
                        }
                        isOpen={openCategories.has("flight-paths")}
                        setIsOpen={(isOpen) => setCategoryIsOpen("flight-paths", isOpen)}
                        className={classNames({
                            "mt-2": hotspotFlights.length > 0,
                        })}
                    />
                )}

                {lines.length > 0 && (
                    <FirePerimeterPluginActionMethodAutoAccordion
                        title="Lines & Polygons"
                        entities={lines}
                        onEntityClicked={({ id }, isSelected) =>
                            setSelectedLineIds((prev) => {
                                if (isSelected) prev.add(id)
                                else prev.delete(id)
                                return new Set(prev)
                            })
                        }
                        isOpen={openCategories.has("lines")}
                        setIsOpen={(isOpen) => setCategoryIsOpen("lines", isOpen)}
                        className={classNames({
                            "mt-2": flightsWithFlightPaths.length > 0 || hotspotFlights.length > 0,
                        })}
                    />
                )}
            </ScrollableArea>

            <FirePerimterPluginActionMethodAutoOffsetInput
                onOffsetChanged={setOffset}
                isOpen={unitDropdownIsOpen}
                setIsOpen={setUnitDropdownIsOpen}
            />

            {shouldShowConfirmControls && (
                <ButtonPrimary
                    title="Confirm"
                    RightIcon={Icons.Checkmark}
                    className="mt-2"
                    disabled={!canConfirm}
                    status={
                        isGeneratingPreviewPolygons ? ButtonStatus.Loading : ButtonStatus.Normal
                    }
                    onClick={() => dispatch(submitPerimeterAuto())}
                />
            )}
        </div>
    )
}
